import { useState, useEffect } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
} from "@mui/material";

/**
 * @typedef {object} SelectOption
 * @property {string|number} value - The value to be submitted
 * @property {string} label - The human-readable text to display
 */

/**
 * @typedef {object} AssignmentInitialData
 * @property {number} warehouse_id - The ID of the warehouse
 * @property {string} shipping_method - The system name of the shipping method
 */

/**
 * Modal for Creating/Editing a WarehouseShippingZone assignment
 * This component is controlled by its parent
 *
 * @param {object} props
 * @param {boolean} props.open - Whether the modal is open
 * @param {function} props.onClose - Function to call when the modal closes (e.g., clicks 'Cancel')
 * @param {function(object): Promise<void>} props.onSubmit - Async function to call with form data ({ warehouse, shipping_method }) when saving
 * @param {boolean} [props.loading=false] - If true, shows a spinner on the submit button
 * @param {string | null} [props.apiError=null] - An error message from the API to display
 * @param {AssignmentInitialData | null} [props.initialData=null] - The data to populate the form with (for "Edit" mode)
 * @param {Array<SelectOption>} [props.warehouseOptions=[]] - Options for the Warehouse select
 * @param {Array<SelectOption>} [props.methodOptions=[]] - Options for the Shipping Method select
 */
const WarehouseAssignmentModal = ({
  open,
  onClose,
  onSubmit,
  loading,
  apiError,
  initialData,
  warehouseOptions = [],
  methodOptions = [],
}) => {
  const [warehouseId, setWarehouseId] = useState("");
  const [shippingMethod, setShippingMethod] = useState("");

  const isEditMode = !!initialData;
  const isFormValid = warehouseId && shippingMethod;

  useEffect(() => {
    if (open) {
      if (isEditMode) {
        setWarehouseId(initialData.warehouse_id || "");
        setShippingMethod(initialData.shipping_method || "");
      } else {
        setWarehouseId("");
        setShippingMethod("");
      }
    }
  }, [open, initialData, isEditMode]);

  const handleSubmit = () => {
    if (!isFormValid) return;
    const payload = {
      warehouse: warehouseId,
      shipping_method: shippingMethod,
    };
    onSubmit(payload);
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>
        {isEditMode ? "Editar Asignación" : "Crear Asignación"}
      </DialogTitle>
      <DialogContent sx={{ width: "100%" }}>
        {apiError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {apiError}
          </Alert>
        )}

        <FormControl fullWidth margin="dense" required>
          <InputLabel id="warehouse-select-label">Bodega (Cliente)</InputLabel>
          <Select
            labelId="warehouse-select-label"
            value={warehouseId}
            label="Bodega (Cliente)"
            onChange={(e) => setWarehouseId(e.target.value)}
            disabled={isEditMode}
          >
            <MenuItem value="">
              <em>Seleccionar...</em>
            </MenuItem>
            {warehouseOptions.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl fullWidth margin="dense" required>
          <InputLabel id="method-select-label">Método de Envío</InputLabel>
          <Select
            labelId="method-select-label"
            value={shippingMethod}
            label="Método de Envío"
            onChange={(e) => setShippingMethod(e.target.value)}
          >
            <MenuItem value="">
              <em>Seleccionar...</em>
            </MenuItem>
            {methodOptions.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Cancelar
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={!isFormValid || loading}
        >
          {loading ? <CircularProgress size={24} /> : "Guardar"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default WarehouseAssignmentModal;
